// The shell every destructive confirmation shares.
//
// What must NOT drift between them is the behaviour: Escape closes, the
// backdrop closes, and neither does while the request is in flight — because a
// dialog that vanishes mid-delete leaves the screen behind it claiming the
// thing is still there. That is subtle, easy to get half-right, and identical
// everywhere, so it lives here.
//
// What must differ is the sentence. That stays with the caller: deleting a post
// and removing one network from it are different losses and are owed different
// words, and a component that tried to parameterise the copy would end up
// saying neither of them well.

import { useEffect, useState, type ReactNode } from "react";

export function ConfirmDialog({
  title, lede, children, confirmLabel, onConfirm, onCancel,
}: {
  title: string;
  /** What is about to go, named — so a list of six is unambiguous. */
  lede?: string;
  /** The warning. The caller's words, not this component's. */
  children: ReactNode;
  confirmLabel: string;
  /** Resolves when the work is done; rejects with the message to show. */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      // Deliberately not clearing `busy`: the caller unmounts this on success,
      // and re-enabling the button first offers a second click on something
      // that is already gone.
    } catch (e: any) {
      setError(e.message ?? String(e));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onCancel()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title">{title}</h2>
        {lede && <p className="modal-lede">{lede}</p>}
        {children}
        {error && <div className="err modal-error">{error}</div>}
        <div className="row modal-actions">
          <button className="danger" disabled={busy} onClick={run}>
            {busy ? "Deleting…" : confirmLabel}
          </button>
          <button onClick={onCancel} disabled={busy}>Keep it</button>
        </div>
      </div>
    </div>
  );
}
